import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProShell } from "@/components/ProShell";
import { LanguageToggle } from "@/components/LanguageToggle";
import api from "@/lib/api";
import { rupee } from "@/lib/constants";
import { PushToggle } from "@/components/PushToggle";
import { IdCard, Phone, Briefcase, MapPin, LogOut, Star, BadgeCheck, Clock, Wallet, ChevronRight, Headphones } from "lucide-react";

export default function ProAccount() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const pro = user?.professional || {};
    const [earn, setEarn] = useState(null);
    const doLogout = async () => { await logout(); nav("/"); };

    useEffect(() => { api.get("/pro/earnings").then((r) => setEarn(r.data)).catch(() => setEarn(false)); }, []);

    return (
        <ProShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-6 text-white">
                <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange text-2xl font-extrabold text-white">{user?.name?.[0]}</div>
                    <div>
                        <h1 className="font-heading text-xl font-extrabold">{user?.name}</h1>
                        <p className="flex items-center gap-1.5 text-sm text-white/60"><Star size={13} className="fill-orange text-orange" /> {pro.rating || "New"} · {pro.pro_id}</p>
                    </div>
                </div>
            </div>

            <div className="space-y-4 px-5 py-5">
                <PushToggle accent="#F97316" />
                {/* Orders & Payouts summary */}
                <div className="rounded-2xl border border-navy/10 bg-white p-4" data-testid="pro-account-summary">
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-navy/40">My orders & payouts</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-orange/5 p-3">
                            <p className="text-[11px] text-navy/45">Total payout (net)</p>
                            <p className="mt-0.5 font-heading text-xl font-extrabold text-orange" data-testid="pro-account-total-payout">{earn ? rupee(earn.total) : "—"}</p>
                        </div>
                        <div className="rounded-xl bg-navy/5 p-3">
                            <p className="text-[11px] text-navy/45">Jobs completed</p>
                            <p className="mt-0.5 font-heading text-xl font-extrabold text-navy" data-testid="pro-account-jobs-completed">{earn ? earn.jobs_completed : "—"}</p>
                        </div>
                    </div>
                    <div className="mt-3 space-y-2">
                        <QuickLink icon={Briefcase} label="My Orders" sub="View your assigned & completed jobs" onClick={() => nav("/pro")} testid="pro-account-orders-link" />
                        <QuickLink icon={Wallet} label="My Payouts" sub="Full payout history & earnings" onClick={() => nav("/pro/earnings")} testid="pro-account-payouts-link" />
                        <QuickLink icon={Headphones} label="Helpdesk" sub="Call, WhatsApp or raise a ticket" onClick={() => nav("/pro/help")} testid="pro-account-help-link" />
                    </div>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-navy/10 bg-white p-4">
                    <span className="text-sm font-semibold text-navy">Language / भाषा</span>
                    <LanguageToggle />
                </div>
                <div className="rounded-2xl border border-navy/10 bg-white p-4">
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-navy/40">Professional profile</h3>
                    <div className="space-y-3">
                        <Info icon={IdCard} label="Pro ID" value={pro.pro_id} />
                        <Info icon={Phone} label="Phone" value={user?.phone} />
                        <Info icon={BadgeCheck} label="Verification" value={pro.verification_status} />
                        <Info icon={Briefcase} label="Services" value={(pro.services || []).join(", ")} />
                        <Info icon={Clock} label="Experience" value={pro.experience} />
                        <Info icon={MapPin} label="Service areas" value={(pro.service_areas || []).join(", ")} />
                    </div>
                </div>
                <button data-testid="pro-logout-btn" onClick={doLogout} className="flex w-full items-center justify-center gap-2 rounded-full border border-red-200 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"><LogOut size={17} /> LOG OUT</button>
            </div>
        </ProShell>
    );
}

function QuickLink({ icon: Icon, label, sub, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-xl border border-navy/10 bg-white p-3 text-left transition-colors hover:bg-navy/[0.03]">
            <div className="rounded-xl bg-orange/10 p-2"><Icon size={18} className="text-orange" /></div>
            <div className="flex-1">
                <p className="text-sm font-bold text-navy">{label}</p>
                <p className="text-xs text-navy/45">{sub}</p>
            </div>
            <ChevronRight size={18} className="text-navy/30" />
        </button>
    );
}

function Info({ icon: Icon, label, value }) {
    return <div className="flex items-center gap-3"><Icon size={17} className="text-navy/40" /><div><p className="text-xs text-navy/45">{label}</p><p className="text-sm font-semibold text-navy">{value || "—"}</p></div></div>;
}
